import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { DraftCapability } from '../api.js';
import type { CrawlOutcome } from '../joblog.js';
import { proposedCount, topUnreached } from '../joblog.js';
import { fmtInt } from './bits.js';

/**
 * What the crawl turned back, on the screen a signed-in customer is sent to.
 *
 * The public console has shown this since the gate existed; the dashboard
 * showed a four-column table of what shipped and nothing at all about what did
 * not. That asymmetry is what makes a thin result mysterious: the one number
 * that explains three tools is the twelve that were proposed, and it was
 * visible only to whoever happened to start the crawl in the other console.
 *
 * Three refusals are shown, and they are three DIFFERENT things — a person who
 * cannot tell them apart cannot act on any of them:
 *
 *  - TURNED BACK. Proposed, executed against the live origin, returned nothing
 *    usable. It does not exist in the manifest and no switch brings it back.
 *    The fix is another crawl, usually of a deeper page.
 *  - SWITCHED OFF. Proposed, executed, returned rows, and then scored below the
 *    run's usefulness threshold — a news index, a vocabulary that exists to
 *    feed another tool's parameter. It IS in the manifest, it is simply not
 *    registered, and the fix is a toggle.
 *  - NEVER REACHED. Not proposed at all, because the page it would have come
 *    from was still in the queue when the page budget ran out. The fix is a
 *    bigger budget, and the crawler names the number.
 *
 * Everything below is read off `job.log` and the draft manifest. Nothing is
 * inferred and nothing is averaged: a section with no rows is not rendered, so
 * an empty screen means the log said nothing rather than that nothing happened.
 */

function pct(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

/**
 * The count that explains the tool count.
 *
 * Printed as one sentence rather than four stat tiles because the numbers only
 * mean anything against each other — "3 tools" is a fact, "12 proposed, 3
 * registered" is the fact a person can act on.
 */
export function GateLedger(props: { outcome: CrawlOutcome; caps: DraftCapability[] }): VNode | null {
  const { outcome, caps } = props;
  if (!outcome.any) return null;
  const declared = proposedCount(outcome);
  const off = caps.filter((c) => !c.register).length;
  const on = caps.length - off;

  return html`
    <p class="gate__tally">
      <b>${fmtInt(declared)}</b> proposed${SP}·${SP}
      <b>${fmtInt(outcome.executed.length)}</b> executed against the origin${SP}·${SP}
      <b class="gate__on">${fmtInt(on)}</b> ${on === 1 ? 'tool ships' : 'tools ship'} switched on
      ${off ? html`${SP}·${SP}<b class="gate__off">${fmtInt(off)}</b> switched off` : null}
      ${outcome.turnedBack.length
        ? html`${SP}·${SP}<b class="gate__back">${fmtInt(outcome.turnedBack.length)}</b> turned back`
        : null}
      ${outcome.unreachedTotal
        ? html`${SP}·${SP}<b>${fmtInt(outcome.unreachedTotal)}</b> ${outcome.unreachedTotal === 1 ? 'page' : 'pages'} never fetched`
        : null}
    </p>
  `;
}

/** Executed against the live origin and refused, with the prober's own sentence. */
function TurnedBack(props: { outcome: CrawlOutcome; origin: string }): VNode | null {
  const rows = props.outcome.turnedBack;
  if (!rows.length) return null;

  return html`
    <section class="gate__sec" aria-labelledby="gate-back-h">
      <h3 class="gate__h" id="gate-back-h">
        Turned back at the gate${SP}<span class="gate__n">${rows.length} not in the manifest</span>
      </h3>
      <p class="gate__p">
        Each of these was proposed, built into a binding and executed against${SP}
        <code>${props.origin}</code>. None returned a row anything could be built on, so none of
        them is in the draft and no switch on any screen brings one back. The gate refusing to ship
        a tool it could not verify is the product working.
      </p>
      <ul class="gate__list">
        ${rows.map(
          (r) => html`<li class="gate__row gate__row--back">
            <code class="gate__id">${r.id}</code>
            <span class="gate__nums">${fmtInt(r.rows)} rows${SP}·${SP}${pct(r.passRate)} pass</span>
            <span class="gate__why">${r.reason ?? 'the prober recorded no reason'}</span>
          </li>`,
        )}
      </ul>
    </section>
  `;
}

/**
 * In the manifest, and not registered on either surface.
 *
 * The arithmetic is printed whole — every signal that moved the score, with its
 * delta — because the judgement is a heuristic and the only person who can tell
 * whether it was wrong is the one who owns the site. A score of −12 against a
 * threshold of 40 with "an index of the site's own news" beside it is something
 * an operator can agree or disagree with; "ranked low" is not.
 */
function SwitchedOff(props: { caps: DraftCapability[]; outcome: CrawlOutcome }): VNode | null {
  const off = props.caps.filter((c) => !c.register);
  const byName = new Map(props.outcome.rankedLow.map((r) => [r.id, r]));
  if (!off.length) return null;

  return html`
    <section class="gate__sec" aria-labelledby="gate-off-h">
      <h3 class="gate__h" id="gate-off-h">
        Shipped switched off${SP}<span class="gate__n">${off.length} below this run's threshold</span>
      </h3>
      <p class="gate__p">
        These verified against the origin and returned rows, and the crawl then scored them as the
        site's own furniture rather than something an agent would want — a news index, a list of
        pages, a vocabulary that exists to fill another tool's parameter. They are in the draft and
        they are not registered on either surface, so an agent will not see them. Nothing is lost:
        the switch is on the site's Tools tab once this draft is published, and turning one on takes
        effect on the next <code>tools/list</code>.
      </p>
      <ul class="gate__list">
        ${off.map((c) => {
          const u = c.usefulness;
          const fallback = byName.get(c.name);
          return html`<li class="gate__row gate__row--off">
            <code class="gate__id">${c.name}</code>
            <span class="gate__nums">
              ${u
                ? html`score ${fmtInt(u.score)} against a threshold of ${fmtInt(u.threshold)}`
                : fallback
                  ? html`score ${fmtInt(fallback.score)}`
                  : 'no score recorded'}
            </span>
            ${u?.reasons.length
              ? html`<ul class="gate__reasons">
                  ${u.reasons.map(
                    (r) => html`<li class="gate__reason">
                      <span class=${r.delta < 0 ? 'gate__delta gate__delta--down' : 'gate__delta'}>
                        ${r.delta > 0 ? `+${r.delta}` : String(r.delta)}
                      </span>
                      <span class="gate__note">${r.note}</span>
                      <code class="gate__signal">${r.signal}</code>
                    </li>`,
                  )}
                </ul>`
              : html`<span class="gate__why"
                  >${fallback?.reason ?? 'the crawl recorded no reason on this run'}</span
                >`}
          </li>`;
        })}
      </ul>
    </section>
  `;
}

/**
 * Ranked, queued, and never fetched.
 *
 * The same site crawled three times gave nine, six and four capabilities, and
 * from the outside the thin runs were indistinguishable from a thin site. This
 * is the difference, and it is the only refusal on this screen with a remedy
 * that is a number rather than a judgement.
 */
function OutOfBudget(props: { outcome: CrawlOutcome }): VNode | null {
  const total = props.outcome.unreachedTotal;
  if (!total) return null;
  // The crawler names at most five of them however many it missed, so this list
  // is a sample and the sentence above it has to say so.
  const rows = topUnreached(props.outcome);

  return html`
    <section class="gate__sec" aria-labelledby="gate-budget-h">
      <h3 class="gate__h" id="gate-budget-h">
        Never reached${SP}<span class="gate__n">${total} ${total === 1 ? 'page' : 'pages'} queued past the budget</span>
      </h3>
      <p class="gate__p">
        The crawl ranked ${total === 1 ? 'this page' : 'these pages'} as somewhere a person does
        something and ran out of its page budget before fetching${SP}
        ${total === 1 ? 'it' : 'them'}. Whatever a tool there would have been was never proposed, so
        it is absent from every count above.${SP}
        ${props.outcome.suggestedPages
          ? html`Crawling the same address again with a budget of${SP}
              <b>${props.outcome.suggestedPages} pages</b> reaches ${total === 1 ? 'it' : 'them'}.`
          : html`Crawling one of these addresses directly is the cheaper fix.`}
      </p>
      <ul class="gate__list">
        ${rows.map(
          (u) => html`<li class="gate__row gate__row--miss">
            <code class="gate__url">${u.url}</code>
            <span class="gate__nums">rank ${u.rank}</span>
          </li>`,
        )}
        ${total > rows.length
          ? html`<li class="gate__more">
              ${total - rows.length} more went unnamed: the crawl prints the five best-ranked and
              counts the rest.
            </li>`
          : null}
      </ul>
    </section>
  `;
}

/**
 * Why a thin result is thin, said in the crawl's own numbers.
 *
 * Only shown where the answer is genuinely thin, and it names the one of the
 * three refusals that dominated rather than listing all three: a person reading
 * "3 tools" wants the largest reason first, and the largest reason is the one
 * with the most rows behind it.
 */
function ThinNotice(props: { outcome: CrawlOutcome; caps: DraftCapability[] }): VNode | null {
  const { outcome, caps } = props;
  const on = caps.filter((c) => c.register).length;
  if (on > 3 || !outcome.any) return null;
  const off = caps.length - on;
  const declared = proposedCount(outcome);
  const missed = outcome.unreachedTotal;

  // A crawl that proposed three tools and shipped all three refused nothing,
  // and this notice exists to name a refusal. Without this line it named the
  // one with the highest count, which on such a run is zero of them, and told
  // an entirely successful crawl that its site hides its answers in a PDF.
  if (declared > 0 && outcome.turnedBack.length + off + missed === 0) return null;

  const dominant =
    outcome.turnedBack.length >= off && outcome.turnedBack.length >= missed
      ? 'turned-back'
      : off >= missed
        ? 'off'
        : 'unreached';

  const because =
    declared === 0
      ? html`Stage 4 proposed nothing, so the gate had nothing to execute. No tool was verified
          because no tool was invented to verify — contact details and prose are not verbs.`
      : dominant === 'turned-back'
        ? html`${outcome.turnedBack.length} of the ${declared} proposals were executed against the
            origin and returned nothing usable. That is a site whose answers live behind a search
            form, in a PDF, or in JavaScript the crawler does not run on somebody else's behalf.`
        : dominant === 'off'
          ? html`${off} of the ${declared} proposals verified and were then scored as the site's own
              furniture. If any of them is a tool you want, it is one toggle away rather than
              another crawl away.`
          : html`${missed} ranked ${missed === 1 ? 'page was' : 'pages were'}${SP}
              still in the queue when the page budget ran out, so whatever was on${SP}
              ${missed === 1 ? 'it' : 'them'} was never proposed.`;

  return html`
    <div class="gate__thin" role="note">
      <p class="gate__thin-h">
        ${on === 0
          ? 'Nothing ships from this crawl.'
          : `${on} ${on === 1 ? 'tool ships' : 'tools ship'} from this crawl.`}
      </p>
      <p class="gate__p">${because}</p>
      <p class="gate__p gate__p--quiet">
        This is the ordinary result rather than an error. Across the 51-site evaluation in${SP}
        <code>docs/COVERAGE.md</code>, 46 sites gave up a brand and 19 produced a capability an
        agent could usefully call. A gate that refuses to ship a tool it could not verify is the
        thing being paid for.
      </p>
    </div>
  `;
}

/**
 * Everything the crawl refused, in one block, under the list of what it kept.
 *
 * Ordered by how far each refusal is from being reversible: turned back is
 * final until another crawl, switched off is a toggle, never reached is a
 * number. A customer scanning downwards therefore meets the cheapest remedy
 * last, having already been told which one is theirs by the notice at the top.
 */
export function CrawlRefusals(props: {
  outcome: CrawlOutcome;
  caps: DraftCapability[];
  origin: string;
}): VNode | null {
  const { outcome, caps, origin } = props;
  if (!outcome.any) return null;

  // A crawl that refused nothing gets no block at all, not an empty one: the
  // heading-shaped gap under a good result is its own kind of bad news.
  const sections = [
    ThinNotice({ outcome, caps }),
    TurnedBack({ outcome, origin }),
    SwitchedOff({ caps, outcome }),
    OutOfBudget({ outcome }),
  ].filter((s): s is VNode => s !== null);
  if (!sections.length) return null;

  return html`<div class="gate">${sections}</div>`;
}
